import { useCallback, useEffect, useRef, useState } from 'react';
import { Play, X, Images as ImagesIcon, Film } from 'lucide-react';
import { PageShell } from '@/components/primitives/PageShell';
import { PageHeader } from '@/components/primitives/PageHeader';
import { EmptyState } from '@/components/primitives/EmptyState';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { getGallery } from '@/api/gallery';
import client from '@/api/client';
import { useAuthedObjectUrl } from '@/hooks/useAuthedObjectUrl';
import { useTenant } from '@/contexts/TenantContext';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 24;

type MediaType = 'all' | 'image' | 'video';

interface GalleryItem {
  id: number | string;
  media_type: 'image' | 'video';
  wildfire_id: number;
  fire_reference?: string | null;
  file_path: string;
  file_url?: string;
  mime_type?: string | null;
  description?: string | null;
  is_approved: boolean;
  is_sensitive: boolean;
  uploaded_at?: string | null;
  duration?: number | null;
}

interface GalleryResponse {
  items: GalleryItem[];
  total: number;
  total_pages: number;
  page: number;
  page_size: number;
}

function GalleryTile({ item, onOpen }: { item: GalleryItem; onOpen: () => void }) {
  const isVideo = item.media_type === 'video';
  // Only blob-load image thumbnails eagerly. Videos would mean downloading the
  // whole file per tile, so show a placeholder and defer the fetch to the
  // lightbox.
  const { url, failed } = useAuthedObjectUrl('image', item.file_path, !isVideo);

  return (
    <button
      type="button"
      onClick={onOpen}
      className="group relative aspect-[4/3] overflow-hidden rounded-md border border-border bg-muted text-left transition hover:opacity-90"
    >
      {isVideo ? (
        <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-muted text-muted-foreground">
          <Film className="h-8 w-8" />
        </div>
      ) : url ? (
        <img
          src={url}
          alt={item.description || `Wildfire image #${item.id}`}
          loading="lazy"
          className="h-full w-full object-cover"
        />
      ) : failed ? (
        <div className="flex h-full w-full items-center justify-center bg-muted text-xs text-muted-foreground">
          Unavailable
        </div>
      ) : (
        <Skeleton className="h-full w-full" />
      )}

      {isVideo && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="rounded-full bg-black/50 p-3 text-white">
            <Play className="h-6 w-6 fill-current" />
          </span>
        </div>
      )}

      {!item.is_approved && (
        <Badge className="absolute right-1 top-1 border-transparent bg-amber-500 text-white hover:bg-amber-500">
          Pending
        </Badge>
      )}

      {(item.fire_reference || item.description) && (
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-3 py-2 text-xs text-white">
          {item.fire_reference && <div className="font-medium">{item.fire_reference}</div>}
          {item.description && <div className="line-clamp-2">{item.description}</div>}
          {item.uploaded_at && (
            <div className="mt-0.5 text-white/70">
              {new Date(item.uploaded_at).toLocaleDateString()}
            </div>
          )}
        </div>
      )}
    </button>
  );
}

function LightboxMedia({ item }: { item: GalleryItem }) {
  const { url, failed } = useAuthedObjectUrl(item.media_type, item.file_path);

  if (failed) {
    return (
      <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
        Failed to load media.
      </div>
    );
  }
  if (!url) {
    return <Skeleton className="h-[50vh] w-full" />;
  }
  if (item.media_type === 'video') {
    return <video src={url} controls autoPlay className="max-h-[70vh] w-full" />;
  }
  return (
    <img
      src={url}
      alt={item.description || `Wildfire image #${item.id}`}
      className="max-h-[70vh] w-full object-contain"
    />
  );
}

export default function FPAGalleryPage() {
  const { tenantId } = useTenant();
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [mediaType, setMediaType] = useState<MediaType>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<GalleryItem | null>(null);
  const reqId = useRef(0);

  const fetchItems = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, unknown> = {
        page,
        page_size: PAGE_SIZE,
        media_type: mediaType,
      };
      if (tenantId) params.tenant_id = tenantId;
      const data = (await getGallery(params)) as GalleryResponse;
      if (id !== reqId.current) return;
      setItems(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (err) {
      if (id !== reqId.current) return;
      console.error('Failed to load gallery:', err);
      setError('Failed to load media. Please try again.');
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [page, mediaType, tenantId]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const changeFilter = (value: string) => {
    setMediaType(value as MediaType);
    setPage(1);
  };

  const goToPage = (p: number) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <PageShell>
      <PageHeader
        title="Gallery"
        subtitle={
          total > 0
            ? `${total} item${total === 1 ? '' : 's'} across your FPA's fires`
            : "Photos and videos from your FPA's fires"
        }
      />

      <Tabs value={mediaType} onValueChange={changeFilter} className="mb-4">
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="image">Photos</TabsTrigger>
          <TabsTrigger value="video">Videos</TabsTrigger>
        </TabsList>
      </Tabs>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 12 }).map((_, i) => (
            <Skeleton key={i} className="aspect-[4/3] w-full rounded-md" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={ImagesIcon}
          title="No media yet"
          description="Photos and videos uploaded to your FPA's fire reports will appear here."
        />
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item) => (
            <GalleryTile
              key={`${item.media_type}-${item.id}`}
              item={item}
              onOpen={() => setSelected(item)}
            />
          ))}
        </div>
      )}

      {!loading && totalPages > 1 && (
        <div className="mt-6 flex justify-center">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn(page === 1 && 'pointer-events-none opacity-50')}
                  onClick={(e) => {
                    e.preventDefault();
                    if (page > 1) goToPage(page - 1);
                  }}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink href="#" isActive onClick={(e) => e.preventDefault()}>
                  {page} / {totalPages}
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn(page === totalPages && 'pointer-events-none opacity-50')}
                  onClick={(e) => {
                    e.preventDefault();
                    if (page < totalPages) goToPage(page + 1);
                  }}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      <Dialog open={Boolean(selected)} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-4xl">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 pr-8">
                  {selected.fire_reference || `Wildfire #${selected.wildfire_id}`}
                  {!selected.is_approved && (
                    <Badge className="border-transparent bg-amber-500 text-white hover:bg-amber-500">
                      Pending moderation
                    </Badge>
                  )}
                </DialogTitle>
              </DialogHeader>
              <LightboxMedia item={selected} />
              {selected.description && (
                <p className="text-sm text-muted-foreground">{selected.description}</p>
              )}
              {selected.uploaded_at && (
                <p className="text-xs text-muted-foreground">
                  Uploaded {new Date(selected.uploaded_at).toLocaleDateString()}
                </p>
              )}
              <div className="flex justify-end">
                <Button variant="ghost" onClick={() => setSelected(null)}>
                  <X className="mr-2 h-4 w-4" /> Close
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
